import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, CornerDownLeft, FileText, LifeBuoy, Moon, Sparkles } from 'lucide-react';
import { useUserSettingsContext } from '../../../context/UserSettingsContext';
import { useTheme } from '../../../context/ThemeContext';
import {
  NATIVE_REPORTS_CATALOG,
  filterCatalogByReportGroups,
  resolveNativeReportGroups,
} from '../../../config/nativeReportsCatalog';
import {
  CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator, CommandShortcut,
} from '../../ui/command';
import { openAssistant, openSupport } from './shellEvents';

const isTypingTarget = (el) =>
  el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable);

/**
 * ⌘K / Ctrl+K (and "/" outside inputs): jump to any page, report or
 * organization. Replaces the floating tray's report search.
 */
export default function CommandPalette({ pages, nav }) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { currentOrganization, userSettings, organizations, setCurrentOrg } = useUserSettingsContext();
  const { toggleDarkMode } = useTheme();

  useEffect(() => {
    const onKey = (e) => {
      if ((e.key || '').toLowerCase() === 'k' && (e.metaKey || e.ctrlKey) && !e.altKey && !e.shiftKey) {
        e.preventDefault();
        setOpen((o) => !o);
        return;
      }
      if (e.key === '/' && !e.metaKey && !e.ctrlKey && !e.altKey && !isTypingTarget(e.target)) {
        e.preventDefault();
        setOpen(true);
      }
    };
    const onOpen = () => setOpen(true);
    document.addEventListener('keydown', onKey);
    window.addEventListener('lodgeic:command-palette:open', onOpen);
    return () => {
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('lodgeic:command-palette:open', onOpen);
    };
  }, []);

  // Report pages with their catalog blurbs, gated by the same report grants.
  const reports = useMemo(() => {
    const groups = resolveNativeReportGroups(currentOrganization, userSettings);
    return filterCatalogByReportGroups(NATIVE_REPORTS_CATALOG, groups).flatMap((g) =>
      g.pages.map((p) => ({ ...p, group: g.group }))
    );
  }, [currentOrganization, userSettings]);

  const reportPaths = useMemo(() => new Set(reports.map((r) => r.path)), [reports]);
  const otherPages = useMemo(
    () => pages.filter((p) => !reportPaths.has(p.to.split('?')[0])),
    [pages, reportPaths]
  );

  const sections = useMemo(() => {
    const map = new Map();
    otherPages.forEach((p) => {
      if (!map.has(p.section)) map.set(p.section, []);
      map.get(p.section).push(p);
    });
    return [...map.entries()];
  }, [otherPages]);

  const orgList = useMemo(
    () => (Array.isArray(organizations) && organizations.length > 1
      ? [...organizations].sort((a, b) => (a.name || '').localeCompare(b.name || ''))
      : []),
    [organizations]
  );

  const aiEnabled = nav.main.some((g) => g.items.some((i) => i.action === 'assistant'));

  const run = (fn) => {
    setOpen(false);
    fn();
  };

  return (
    <CommandDialog open={open} onOpenChange={setOpen} label="Search Lodgeic">
      <CommandInput placeholder="Search reports, pages, organizations…" />
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>

        {reports.length > 0 && (
          <CommandGroup heading="Reports">
            {reports.map((r) => {
              const Icon = r.icon || FileText;
              return (
                <CommandItem key={r.key} value={`report ${r.group} ${r.title} ${r.blurb || ''}`} onSelect={() => run(() => navigate(r.path))}>
                  <Icon />
                  <span>{r.title}</span>
                  <span className="truncate text-xs text-[var(--lodge-onyx-400)]">{r.group}</span>
                  <CommandShortcut><CornerDownLeft className="h-3 w-3" /></CommandShortcut>
                </CommandItem>
              );
            })}
          </CommandGroup>
        )}

        {sections.map(([section, items]) => (
          <CommandGroup key={section} heading={section}>
            {items.map((p) => (
              <CommandItem key={p.key} value={`${section} ${p.parent || ''} ${p.title}`} onSelect={() => run(() => navigate(p.to))}>
                <FileText />
                <span>{p.parent ? `${p.parent} · ${p.title}` : p.title}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        ))}

        <CommandSeparator />
        <CommandGroup heading="Actions">
          {aiEnabled && (
            <CommandItem value="ask lodgeic ai assistant" onSelect={() => run(openAssistant)}>
              <Sparkles />
              <span>Ask Lodgeic AI</span>
            </CommandItem>
          )}
          <CommandItem value="toggle dark mode theme" onSelect={() => run(toggleDarkMode)}>
            <Moon />
            <span>Toggle dark mode</span>
          </CommandItem>
          <CommandItem value="contact support help intercom" onSelect={() => run(openSupport)}>
            <LifeBuoy />
            <span>Contact support</span>
          </CommandItem>
        </CommandGroup>

        {orgList.length > 0 && (
          <CommandGroup heading="Switch organization">
            {orgList.map((org) => (
              <CommandItem
                key={org.code}
                value={`organization ${org.name} ${org.code}`}
                onSelect={() => run(() => {
                  setCurrentOrg(org.code);
                  navigate('/dashboard');
                })}
              >
                <Building2 />
                <span>{org.name}</span>
                {currentOrganization?.code === org.code && (
                  <span className="ml-auto text-xs text-[var(--lodge-orange-500)]">Current</span>
                )}
              </CommandItem>
            ))}
          </CommandGroup>
        )}
      </CommandList>
    </CommandDialog>
  );
}
